import { Icon } from '@iconify/react';
import { useState } from 'react';

import { Botao, Campo } from '../components/Sga';
import { api, ErroApi } from '../lib/api';

/** Piso do tamanho da senha — o mesmo que o gateway exige. */
const SENHA_MIN = 10;

/**
 * Recuperação de senha, nas duas pontas do fluxo.
 *
 * `pedir`     — a pessoa digita o e-mail e recebe o link.
 * `redefinir` — a pessoa voltou pelo link do e-mail, com o token na URL.
 *
 * Vale só para usuário de login local. Quem entra pelo SSO do SGA Desk não tem
 * senha aqui: a credencial dele vive no sistema de origem.
 */
export function RecuperarSenha({
  token,
  aoVoltar,
}: {
  token?: string | null;
  aoVoltar: () => void;
}) {
  return (
    <div className="sga-login">
      <div className="sga-login__card">
        <div className="sga-login__head">
          <img src="/logos/sga-logotipo-branco.svg" alt="SGA Petro" style={{ height: 30 }} />
          <div style={{ fontSize: 19, fontWeight: 500, marginTop: 10 }}>
            {token ? 'Nova senha' : 'Recuperar acesso'}
          </div>
        </div>
        {token ? <FormRedefinir token={token} aoVoltar={aoVoltar} /> : <FormPedir aoVoltar={aoVoltar} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ etapa 1: pedir

function FormPedir({ aoVoltar }: { aoVoltar: () => void }) {
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    setOcupado(true);
    try {
      await api.post('/auth/senha/esqueci', { email: email.trim() });
      setEnviado(true);
    } catch (err) {
      // O gateway responde igual para e-mail cadastrado ou não — o único erro
      // que chega aqui é de configuração (SMTP ausente) ou rede.
      setErro(
        err instanceof ErroApi && err.codigo === 'smtp_ausente'
          ? 'O envio de e-mail ainda não está configurado neste ambiente. Fale com a infra.'
          : 'Não consegui falar com o servidor. Tente de novo em instantes.',
      );
    } finally {
      setOcupado(false);
    }
  };

  if (enviado) {
    return (
      <div className="sga-login__body">
        <div
          className="sga-aviso-warn"
          style={{ background: 'rgba(19,180,124,.12)', color: 'var(--sga-primary)' }}
        >
          <Icon icon="mdi:email-check-outline" width={17} />
          Se este e-mail tiver cadastro, o link chega em instantes.
        </div>
        <span className="hint">
          O link vale por 1 hora e só funciona uma vez. Confira também a caixa de spam.
        </span>
        <Botao onClick={aoVoltar} style={{ height: 40 }}>
          Voltar ao login
        </Botao>
      </div>
    );
  }

  return (
    <form className="sga-login__body" onSubmit={enviar}>
      {erro && (
        <div
          className="sga-aviso-warn"
          style={{ background: 'rgba(255,77,79,.12)', color: 'var(--sga-error)' }}
        >
          <Icon icon="mdi:alert-circle-outline" width={17} />
          {erro}
        </div>
      )}

      <span className="hint">
        Informe o e-mail da sua conta. Enviamos um link para você escolher uma senha nova.
      </span>

      <Campo rotulo="E-mail" obrigatorio>
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          autoFocus
        />
      </Campo>

      <Botao variante="primary" type="submit" disabled={ocupado} style={{ height: 40, fontSize: 15 }}>
        {ocupado ? 'Enviando…' : 'Enviar link'}
      </Botao>

      <Botao type="button" onClick={aoVoltar} style={{ height: 36 }}>
        Voltar
      </Botao>
    </form>
  );
}

// -------------------------------------------------------- etapa 2: redefinir

function FormRedefinir({ token, aoVoltar }: { token: string; aoVoltar: () => void }) {
  const [senha, setSenha] = useState('');
  const [repetir, setRepetir] = useState('');
  const [pronto, setPronto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const curta = senha.length > 0 && senha.length < SENHA_MIN;
  const divergem = repetir.length > 0 && senha !== repetir;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (senha !== repetir) {
      setErro('As duas senhas precisam ser iguais.');
      return;
    }
    setOcupado(true);
    try {
      await api.post('/auth/senha/redefinir', { token, senha });
      setPronto(true);
    } catch (err) {
      // 400 aqui é link vencido, já usado ou senha curta — o gateway manda a
      // mensagem exata, que é mais útil que um texto genérico nosso.
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não consegui falar com o servidor. Tente de novo em instantes.',
      );
    } finally {
      setOcupado(false);
    }
  };

  if (pronto) {
    return (
      <div className="sga-login__body">
        <div
          className="sga-aviso-warn"
          style={{ background: 'rgba(19,180,124,.12)', color: 'var(--sga-primary)' }}
        >
          <Icon icon="mdi:check-circle-outline" width={17} />
          Senha alterada. As outras sessões foram encerradas.
        </div>
        <Botao variante="primary" onClick={aoVoltar} style={{ height: 40 }}>
          Entrar
        </Botao>
      </div>
    );
  }

  return (
    <form className="sga-login__body" onSubmit={enviar}>
      {erro && (
        <div
          className="sga-aviso-warn"
          style={{ background: 'rgba(255,77,79,.12)', color: 'var(--sga-error)' }}
        >
          <Icon icon="mdi:alert-circle-outline" width={17} />
          {erro}
        </div>
      )}

      <Campo rotulo="Nova senha" obrigatorio>
        <input
          className="input"
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="new-password"
          required
          autoFocus
        />
      </Campo>

      <Campo rotulo="Repita a nova senha" obrigatorio>
        <input
          className="input"
          type="password"
          value={repetir}
          onChange={(e) => setRepetir(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Campo>

      <span className="hint" style={{ color: curta || divergem ? 'var(--sga-error)' : undefined }}>
        {curta
          ? `Faltam ${SENHA_MIN - senha.length} caracteres.`
          : divergem
            ? 'As duas senhas estão diferentes.'
            : `Pelo menos ${SENHA_MIN} caracteres. Uma frase que só você usa vale mais que símbolos.`}
      </span>

      <Botao
        variante="primary"
        type="submit"
        disabled={ocupado || curta || divergem || senha.length === 0}
        style={{ height: 40, fontSize: 15 }}
      >
        {ocupado ? 'Salvando…' : 'Salvar senha'}
      </Botao>

      <Botao type="button" onClick={aoVoltar} style={{ height: 36 }}>
        Cancelar
      </Botao>
    </form>
  );
}
